/**
 * Error boundary component for catching and displaying React render errors.
 *
 * Catches JavaScript errors anywhere in the child component tree, logs them,
 * and renders a fallback UI instead of crashing the entire application.
 *
 * @module utils/ErrorBoundary
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from "prop-types";

/**
 * A React component that serves as an error boundary to catch JavaScript errors
 * in its child component tree, log those errors, and display a fallback UI instead of crashing.
 *
 * This component is useful for gracefully handling rendering errors in React applications.
 *
 * Methods:
 * - `getDerivedStateFromError(error)`: Updates the state to set an error flag when an error is caught.
 * - `componentDidCatch(error, errorInfo)`: Logs error details and additional information for debugging.
 *
 * Props:
 * - Accepts standard React component props and renders its children unless an error occurs.
 *
 * State:
 * - `hasError`: A boolean flag that indicates whether an error has been caught.
 * - `error`: Stores the error object when an error is caught.
 */
export default class ErrorBoundary extends React.Component {
    /**
     * Initializes a new instance of the component with the given properties.
     * The constructor sets the initial state of the component.
     *
     * @param {Object} props - The properties passed to the component.
     * @return {void} Does not return a value.
     */
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    /**
     * Invoked after an error has been thrown during rendering, in a lifecycle method, or in the constructor of a descendant component.
     * This method is used to update the state based on the error thrown, enabling the display of a fallback UI.
     *
     * @param {Error} error - The error that was thrown.
     * @return {Object} An object representing the updated state, typically used to render a fallback UI.
     */
    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error: error };
    }

    /**
     * Invoked when an error is thrown during rendering in a child component.
     * This method is used for handling errors gracefully and possibly logging them for debugging purposes.
     *
     * @param {Error} error - The error that was thrown.
     * @param {Object} errorInfo - Additional information about the error, including the component stack trace.
     * @return {void} This method does not return a value.
     */
    componentDidCatch(error, errorInfo) {
        // You can also log the error to an error reporting service
        console.log('Error = %o', error);
        console.log('ErrorInfo = %o', errorInfo);
    }

    /**
     * Renders the component, providing a custom fallback UI if an error is detected in the component state.
     * If no error is present, it renders the component's children.
     *
     * @return {React.ReactNode} Returns a fallback UI when an error occurs, or the component's children otherwise.
     */
    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return <h1>Something went wrong.</h1>;
        }

        return this.props.children;
    }
}

/**
 * Defines the prop types for the ErrorBoundary component.
 *
 * propTypes:
 * - children: A React node that represents the child components of the ErrorBoundary.
 * - fallback: An optional React node to be displayed if an error occurs within the child components.
 *   If not provided, a default fallback UI might be rendered.
 */
ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
};
